import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../lib/http/apiClient";
import { parseApiError } from "../lib/http/apiErrors";

interface FactPill {
  value?: string;
  label?: string;
}

interface PublicPartner {
  id: string;
  owner_user_id: string;
  organization_name: string;
  partner_type: string;
  is_verified: boolean;
  status: string;
  brand_logo: string;
  channel_banner: string;
  destinations: string[];
  follower_count: number;
  primary_stat: FactPill | null;
  fact_pills: string[];
  showcase_media: { url: string; type?: string }[];
}

const typeLabel = (t: string) => (t || "").replace(/_/g, " ");

const PartnerProfilePage: React.FC = () => {
  const { ownerId = "" } = useParams();
  const navigate = useNavigate();

  const [partner, setPartner] = useState<PublicPartner | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await apiClient.get<PublicPartner>(`/api/v1/partner/by-owner/${ownerId}`);
      setPartner(data);
    } catch (e) {
      setErr(parseApiError(e).message);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="h-40 bg-gray-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (err || !partner) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate("/")} className="text-sm text-gray-500 hover:text-gray-800 mb-4">
          ← Back
        </button>
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {err || "Partner not found."}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <button onClick={() => navigate("/")} className="text-sm text-gray-500 hover:text-gray-800 mb-4">
        ← Back
      </button>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="h-36 sm:h-44 bg-gray-100 relative">
          {partner.channel_banner ? (
            <img src={partner.channel_banner} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-indigo-100 via-gray-100 to-cyan-100" />
          )}
        </div>

        <div className="px-6 pb-6 relative z-10">
          <div className="flex items-end gap-4 -mt-10 mb-3">
            <div className="w-20 h-20 rounded-full bg-gray-100 border-4 border-white overflow-hidden flex items-center justify-center shrink-0 shadow-sm">
              {partner.brand_logo ? (
                <img src={partner.brand_logo} alt={partner.organization_name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-gray-400">
                  {partner.organization_name?.[0]?.toUpperCase() || "P"}
                </span>
              )}
            </div>
            <div className="min-w-0 pb-1">
              <h1 className="text-xl font-semibold text-gray-900 truncate">{partner.organization_name}</h1>
              {partner.partner_type && (
                <p className="text-sm text-gray-500 capitalize truncate">{typeLabel(partner.partner_type)}</p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            {partner.is_verified && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-50 text-emerald-700">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Verified partner
              </span>
            )}
            {partner.follower_count > 0 && (
              <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-gray-100 text-gray-600">
                {partner.follower_count.toLocaleString()} followers
              </span>
            )}
            {partner.primary_stat?.value && (
              <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-gray-100 text-gray-600">
                {partner.primary_stat.value} {partner.primary_stat.label}
              </span>
            )}
            {(partner.fact_pills || []).map((p, i) => (
              <span key={i} className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-gray-100 text-gray-600">
                {p}
              </span>
            ))}
          </div>

          {partner.destinations?.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-medium text-gray-400 mb-1.5">Key destinations</div>
              <div className="flex flex-wrap gap-1.5">
                {partner.destinations.map((d, i) => (
                  <span key={i} className="px-2 py-1 rounded-md text-xs bg-gray-50 border border-gray-200 text-gray-600">
                    {d}
                  </span>
                ))}
              </div>
            </div>
          )}

          {partner.showcase_media?.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-400 mb-1.5">Showcase</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {partner.showcase_media.map((m, i) => (
                  <div key={i} className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                    {m.type === "video" ? (
                      <video src={m.url} className="w-full h-full object-cover" controls />
                    ) : (
                      <img src={m.url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerProfilePage;
